import { AccessCardTypeEnum } from '@zaoblako/shared/modules/pacs/enums/access-card-type.enum';
import { AccessCardTypeEnumI18n } from '@zaoblako/shared/modules/pacs/i18n/access-card-type-enum.i18n';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { PacsDtoKeysEnum } from '@zaoblako/shared/modules/pacs/enums/pacs-dto-keys.enum';
import { DtoMessageEnum } from '@zaoblako/shared/common/enums/dto.message.enum';
import { commonEnumValidator } from '@zaoblako/shared/modules/serde/validators/common-enum.validator';
import { I18nLocalesEnum } from '@zaoblako/shared/common/enums/i18n.locales.enum';
import { entityLikeSchema } from '@zaoblako/shared/modules/serde/schemas/entity-like.schema';
import { SecurityBackendValidatorsEnum } from '@zaoblako/shared/modules/security/enums/security-backend-validators.enum';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import type { OnlyIdRequiredInterface } from '@zaoblako/shared/common/interfaces/only-id-required.interface';

import * as yup from 'yup';

/** Access card create/update request body */
export interface AccessCardFormInterface {
  /** Card unique code */
  code: string;
  /** Card holder */
  user?: OnlyIdRequiredInterface<UserFindInterface>;
  /** Card holder */
  externalUser?: OnlyIdRequiredInterface<ExternalUserFindInterface>;
  /** Card type */
  type: AccessCardTypeEnum;
  /** Card expiry date */
  begin?: Date;
  end?: Date;
  /** Blocking Card */
  isBlocking?: boolean;
  /** Set active status Card */
  isActive?: boolean;
  /** Maximum number of passes on the card */
  maxPasses?: number;
}

/** Access card create/update request body - DTO schema */
export const AccessCardFormDto = SerdeDtoClass.create<AccessCardFormInterface>(PacsDtoKeysEnum.ACCESS_CARD_FORM_DTO, {
  code: {
    label: 'Код',
    serializer: serdeContext => yup
      .string()
      .test({
        test: (value, context) => {
          if (!value) {
            return context.createError({ message: 'Код карты обязателен' });
          }
          const sType = AccessCardTypeEnumI18n[serdeContext?.data?.locale || I18nLocalesEnum.RU_RU];

          switch (context.parent.type) {
            case AccessCardTypeEnum.TEST_MOCK_CARD:
              return true;
            case AccessCardTypeEnum.EM_MARINE: {
              if (!/\d+,\d+/igm.test(value)) {
                return context.createError({ message: `Код карты типа "${sType}" должен быть в формате "999,99999"` });
              } else {
                return true;
              }
            };
            case AccessCardTypeEnum.MIFARE: {
              if (!/[0-9a-fA-F]/igm.test(value)) {
                return context.createError({ message: `Код карты типа "${sType}" должен содержать только числа и латинские буквы от A до F` });
              } else {
                return true;
              }
            };
          }
        },
      })
      .min(1, DtoMessageEnum.EMPTY_STRING),
  },
  user: {
    optional: true,
    label: 'Пользователь',
    serializer: entityLikeSchema,
    checkers: [SecurityBackendValidatorsEnum.USER_ASSERT_EXISTS_BY_DTO],
  },
  externalUser: {
    optional: true,
    label: 'Пользователь',
    serializer: entityLikeSchema,
    checkers: [SecurityBackendValidatorsEnum.EXTERNAL_USER_ASSERT_EXISTS_BY_DTO],
  },
  type: {
    label: 'Тип карты (протокол)',
    serializer: commonEnumValidator(AccessCardTypeEnum, AccessCardTypeEnumI18n),
  },
  isBlocking: {
    optional: true,
    label: 'Блокирующая карта',
    serializer: yup.boolean(),
  },
  isActive: {
    optional: true,
    label: 'Активная карта',
    serializer: yup.boolean(),
  },
  maxPasses: {
    optional: true,
    label: 'Максимальное количество проходов',
    serializer: yup.number(),
  },
  begin: {
    optional: true,
    label: 'Начало действия карты',
    serializer: yup.date(),
  },
  end: {
    optional: true,
    label: 'Окончание действия карты',
    serializer: yup.date(),
  },
});
